import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  LaeProdCycleOption,
  LaeProdGranularity,
  LaeProdSource,
  LaeProductivityQuery,
} from '@/lib/types';
import {
  addDays,
  computeRange,
  granularityOf,
  isIsoDate,
  previousWindow,
  todayIst,
  type LaeDateRange,
} from './lae-dates';

/**
 * LAE Productivity (Schools mode): every page control lives in the URL.
 *
 * Ported from the LAE app `src/features/productivity/useProductivityParams.js`, Schools dashboard
 * subset only. URL keys (spec 10.4): `cycle`, `funnel`, `source`, `p`, `d`, `from`, `to`, `school`,
 * `schoolFunnel`. Every default is an ABSENT key, nothing is written on arrival, and every write is
 * one `setSearchParams(..., { replace: true })` that keeps unrelated keys.
 *
 * Deliberate differences from LAE:
 * - Schools mode only: no `mode`, `region`, `rep`, `hc`, `sort` or `school_id` filter. Sort lives in
 *   account memory, not in the URL (spec 10.4).
 * - The modal keys are `school` / `schoolFunnel` (LAE `os` / `of`).
 * - NO hard-coded default cycle (owner, 2026-10-29). An absent `cycle` key means "the current one":
 *   - LAE cycle labels are business cycles, `YYYY-YY`, running 1 April to 31 March (backend-app
 *     `d_business_cycles`, example start 2024-04-01; `ScholarshipPipelineService::currentCycleId`
 *     picks the cycle covering India today). The wire has no current flag (`LaeProdCycleOption` is
 *     `{ value, count }`), so `resolveDefaultCycle` picks the listed cycle whose 1 Apr..31 Mar span
 *     covers India today, falling back like `currentCycleId` (see there).
 *   - First request: the response's `cycles[]` does NOT depend on the `cycle` param (backend-app
 *     `LaeProductivityScope::cycles` reads only the visible regions), and an absent `cycle` upstream
 *     means EVERY cycle. So, rather than a wasted all-cycles request, the first request already
 *     carries today's cycle label computed from the date (`cycleLabelOf(today)`). When the response
 *     lists that cycle (the normal case) nothing re-requests; when it does not, the resolved default
 *     changes and the page re-requests once with it. `cycleResolved` says whether the default is
 *     settled from real data (or an explicit URL value), so the page can hold the strip until then.
 *   - The hook remembers the last cycles list it was given, so a page that drops `data` while a new
 *     request loads never flips the default back to the guess (that would loop).
 * - A legacy numeric `?cycle=3` (SEED FAB cycle id, the old page) is never sent upstream: only
 *   `all` or `YYYY-YY` is read; anything else reads as the default.
 * - Legacy funnel aliases from the old page are read (`prospect`, `admit`), and `funnel=all` reads as
 *   All funnels (the Schools dashboard default). Only the funnels the EMERGE proxy accepts are read
 *   (`LaeFunnel`: `prospect_to_application`, `admit_to_enrollment`); any other value reads as All
 *   funnels, so a stale or hand-typed key never becomes a 422.
 * - `resetFilters` also resets the period (owner brief), not only cycle / funnel / source.
 */

/** Period presets, in display order. URL key `p`; absent = `12w` (Schools default). */
export const PRESETS = ['day', '7d', '30d', '12w', 'custom'] as const;
export type LaePeriodPreset = (typeof PRESETS)[number];
export const DEFAULT_PRESET: LaePeriodPreset = '12w';

/** URL key `source`: which calls count. Absent = `all`. */
export const SOURCES = ['all', 'system', 'manual'] as const satisfies readonly LaeProdSource[];

/** Legacy / shorthand funnel values read from the URL. `''` = All funnels. */
export const FUNNEL_ALIASES: Readonly<Record<string, string>> = {
  all: '',
  prospect: 'prospect_to_application',
  admit: 'admit_to_enrollment',
};

/** The funnel slugs the EMERGE proxy accepts (besides `all`); `LaeFunnel` in types.ts. */
export const FUNNELS = ['prospect_to_application', 'admit_to_enrollment'] as const;
/** A funnel in view: `''` = All funnels. */
export type LaeFunnelValue = '' | (typeof FUNNELS)[number];

/** A raw URL / picker value → a funnel the proxy accepts; aliases resolved, anything else = All funnels. */
export function funnelOf(raw: string | null | undefined): LaeFunnelValue {
  if (!raw) return '';
  const v = FUNNEL_ALIASES[raw] ?? raw;
  return (FUNNELS as readonly string[]).includes(v) ? (v as LaeFunnelValue) : '';
}

const CYCLE_RE =/^(\d{4})-(\d{2})$/;

/** `"2026 - 27"` → `"2026-27"`: the upstream compares cycles with spaces stripped on both sides. */
export function normaliseCycle(v: string): string {
  return v.replace(/\s+/g, '');
}

/** The LAE business cycle containing an India day: 1 April..31 March, e.g. 2026-10-29 → `2026-27`. */
export function cycleLabelOf(isoDay: string): string {
  const y = Number(isoDay.slice(0, 4));
  const start = isoDay.slice(5) >= '04-01' ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** First India day of a `YYYY-YY` cycle, or null when the label is not that shape. */
function cycleStartOf(label: string): string | null {
  const m = CYCLE_RE.exec(normaliseCycle(label));
  return m ? `${m[1]}-04-01` : null;
}

type CycleEntry = LaeProdCycleOption | string;

function cycleValueOf(c: CycleEntry): string {
  return normaliseCycle(typeof c === 'string' ? c : c.value);
}

/**
 * The current cycle, from the response's `cycles[]` (newest first) and India today:
 * - list unknown (`null` / `undefined`): today's label from the date (the first-request guess);
 * - empty list: `null` (no cycles at all; the request then omits `cycle` = every cycle);
 * - the listed cycle whose 1 Apr..31 Mar span covers today;
 * - else the newest listed cycle that started on or before today (`currentCycleId` fallback);
 * - else the earliest listed cycle (every cycle is in the future);
 * - else (no label parses as `YYYY-YY`) the first listed value.
 * Values come back normalised (spaces stripped).
 */
export function resolveDefaultCycle(
  cycles: readonly CycleEntry[] | null | undefined,
  today: string,
): string | null {
  if (cycles === null || cycles === undefined) return cycleLabelOf(today);
  const values = cycles.map(cycleValueOf).filter((v) => v !== '');
  if (values.length === 0) return null;
  const current = cycleLabelOf(today);
  if (values.includes(current)) return current;
  const dated = values
    .map((v) => ({ v, start: cycleStartOf(v) }))
    .filter((x): x is { v: string; start: string } => x.start !== null)
    .sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : 0));
  const started = dated.find((x) => x.start <= today);
  if (started) return started.v;
  const earliest = dated[dated.length - 1];
  if (earliest) return earliest.v;
  return values[0] ?? null;
}

/** The inputs of one Schools list request. `cycle` / `funnel` `''` = all. */
export interface LaeParamsState {
  range: LaeDateRange;
  granularity: LaeProdGranularity;
  cycle: string;
  funnel: LaeFunnelValue;
  source: LaeProdSource;
}

/**
 * The list request (LAE `apiParams`, Schools dashboard): `from`, `to`, `granularity` always;
 * `cycle` only when one is set; `funnel` ALWAYS (absent upstream = `prospect_to_application`, so All
 * funnels is sent as `all`); `source` only when narrowed; `breakdown=assignments` always.
 */
export function toApiParams(state: LaeParamsState): LaeProductivityQuery {
  const p: LaeProductivityQuery = {
    from: state.range.from,
    to: state.range.to,
    granularity: state.granularity,
  };
  if (state.cycle) p.cycle = state.cycle;
  p.funnel = state.funnel || 'all';
  if (state.source !== 'all') p.source = state.source;
  p.breakdown = 'assignments';
  return p;
}

/** A period change, as `PeriodControl` sends it (LAE `setPeriod({ preset, day?, from?, to? })`). */
export interface LaePeriodChange {
  preset?: string;
  day?: string;
  from?: string;
  to?: string;
}

export interface LaeProductivityParamsOptions {
  /** The latest response's `cycles[]`; omit or pass null while none has arrived. */
  cycles?: readonly CycleEntry[] | null;
}

export interface LaeProductivityParams {
  /** The effective cycle, normalised; `''` = All cycles. */
  cycle: string;
  /** `''` = All cycles; the current cycle clears the key. */
  setCycle: (v: string) => void;
  /** The current cycle (`resolveDefaultCycle`); null = no cycles, so the default is All cycles. */
  defaultCycle: string | null;
  /** True when the cycle in view is the default (no chip). */
  cycleIsDefault: boolean;
  /** True once the default is settled from a cycles list, or the URL names a cycle explicitly. */
  cycleResolved: boolean;
  /** `''` = All funnels (the default). Only the proxy's funnels are read; anything else = All. */
  funnel: LaeFunnelValue;
  /** `''` = All funnels; an unknown value also clears the key. */
  setFunnel: (v: string) => void;
  source: LaeProdSource;
  setSource: (v: string) => void;
  preset: LaePeriodPreset;
  /** The picked day for the `day` preset (India today when none). */
  day: string;
  /** India today. */
  today: string;
  range: LaeDateRange;
  granularity: LaeProdGranularity;
  setPeriod: (change: LaePeriodChange) => void;
  /** Move the day by n; refused past India today. Puts the page on the `day` preset. */
  stepDay: (n: number) => void;
  /** The school whose modal is open (URL `school`); null = closed. */
  openSchool: string | null;
  /** The funnel of the row that opened it (URL `schoolFunnel`); `''` = none given (or unknown). */
  openSchoolFunnel: LaeFunnelValue;
  setOpenSchool: (schoolId: string | null, funnel?: string | null) => void;
  /** Clears cycle, funnel, source and the period. */
  resetFilters: () => void;
  /** The list request, with `breakdown=assignments`. */
  apiParams: LaeProductivityQuery;
  /** The previous equal-length window, same cycle / funnel / source / granularity, no `breakdown`. */
  prevParams: LaeProductivityQuery | null;
}

function isPreset(v: string | null): v is LaePeriodPreset {
  return v !== null && (PRESETS as readonly string[]).includes(v);
}

function isSource(v: string | null): v is LaeProdSource {
  return v !== null && (SOURCES as readonly string[]).includes(v);
}

/** Remembered cycles list: `sig` is its normalised JSON, so an equal list never re-sets state. */
interface KnownCycles {
  sig: string;
  values: string[];
}

export function useLaeProductivityParams(options: LaeProductivityParamsOptions = {}): LaeProductivityParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const T = todayIst();

  // Keep the last cycles list given (derived state, set during render only when it changes).
  const incoming = options.cycles;
  const incomingValues = incoming === null || incoming === undefined ? null : incoming.map(cycleValueOf);
  const incomingSig = incomingValues === null ? null : JSON.stringify(incomingValues);
  const [known, setKnown] = useState<KnownCycles | null>(null);
  if (incomingSig !== null && incomingValues !== null && incomingSig !== known?.sig) {
    setKnown({ sig: incomingSig, values: incomingValues });
  }
  const knownValues =
    incomingValues !== null ? incomingValues : known === null ? null : known.values;
  const defaultCycle = resolveDefaultCycle(knownValues, T);

  const rawCycle = searchParams.get('cycle');
  const explicitCycle =
    rawCycle === 'all' ? '' : rawCycle !== null && CYCLE_RE.test(normaliseCycle(rawCycle)) ? normaliseCycle(rawCycle) : null;
  const cycle = explicitCycle !== null ? explicitCycle : (defaultCycle ?? '');
  const cycleIsDefault = cycle === (defaultCycle ?? '');
  const cycleResolved = explicitCycle !== null || knownValues !== null;

  const funnel = funnelOf(searchParams.get('funnel'));

  const rawSource = searchParams.get('source');
  const source: LaeProdSource = isSource(rawSource) ? rawSource : 'all';

  const rawPreset = searchParams.get('p');
  const preset: LaePeriodPreset = isPreset(rawPreset) ? rawPreset : DEFAULT_PRESET;

  const rawDay = searchParams.get('d');
  const day = isIsoDate(rawDay) && rawDay <= T ? rawDay : T;

  const rawFrom = searchParams.get('from');
  const rawTo = searchParams.get('to');
  const range = useMemo(
    () => computeRange(preset, day, rawFrom, rawTo, T),
    [preset, day, rawFrom, rawTo, T],
  );
  const granularity = granularityOf(range.from, range.to);

  const openSchool = searchParams.get('school') || null;
  const openSchoolFunnel = openSchool ? funnelOf(searchParams.get('schoolFunnel')) : '';

  /** One URL write per call: mutate a copy of the current params, then replace. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setCycle = useCallback(
    (v: string) =>
      update((next) => {
        const value = normaliseCycle(v);
        if (value === (defaultCycle ?? '')) next.delete('cycle');
        else if (!value) next.set('cycle', 'all');
        else next.set('cycle', value);
      }),
    [update, defaultCycle],
  );

  const setFunnel = useCallback(
    (v: string) =>
      update((next) => {
        const value = funnelOf(v);
        if (!value) next.delete('funnel');
        else next.set('funnel', value);
      }),
    [update],
  );

  const setSource = useCallback(
    (v: string) =>
      update((next) => {
        if (v === 'system' || v === 'manual') next.set('source', v);
        else next.delete('source');
      }),
    [update],
  );

  const setPeriod = useCallback(
    ({ preset: np, day: nd, from: nf, to: nt }: LaePeriodChange) =>
      update((next) => {
        const p: LaePeriodPreset = np !== undefined && isPreset(np) ? np : preset;
        if (p === DEFAULT_PRESET) next.delete('p');
        else next.set('p', p);

        if (p === 'day') {
          const d = nd !== undefined ? nd : next.get('d') || T;
          if (isIsoDate(d) && d < T) next.set('d', d);
          else next.delete('d');
        } else {
          next.delete('d');
        }

        if (p === 'custom') {
          next.set('from', isIsoDate(nf) ? nf : range.from);
          next.set('to', isIsoDate(nt) ? nt : range.to);
        } else {
          next.delete('from');
          next.delete('to');
        }
      }),
    [update, preset, T, range.from, range.to],
  );

  const stepDay = useCallback(
    (n: number) => {
      const nd = addDays(day, n);
      if (nd > T) return;
      update((next) => {
        // The Schools default preset is `12w`, so `day` is always written.
        next.set('p', 'day');
        if (nd < T) next.set('d', nd);
        else next.delete('d');
        next.delete('from');
        next.delete('to');
      });
    },
    [update, day, T],
  );

  const setOpenSchool = useCallback(
    (schoolId: string | null, rowFunnel?: string | null) =>
      update((next) => {
        if (schoolId === null || schoolId === '') {
          next.delete('school');
          next.delete('schoolFunnel');
          return;
        }
        next.set('school', String(schoolId));
        if (rowFunnel) next.set('schoolFunnel', String(rowFunnel));
        else next.delete('schoolFunnel');
      }),
    [update],
  );

  const resetFilters = useCallback(
    () =>
      update((next) => {
        ['cycle', 'funnel', 'source', 'p', 'd', 'from', 'to'].forEach((k) => next.delete(k));
      }),
    [update],
  );

  const apiParams = useMemo(
    () => toApiParams({ range, granularity, cycle, funnel, source }),
    [range, granularity, cycle, funnel, source],
  );

  const prevParams = useMemo<LaeProductivityQuery | null>(() => {
    const { breakdown: _breakdown, ...rest } = apiParams;
    const win = previousWindow(range.from, range.to);
    if (win === null) return null;
    // Granularity is copied from the current request, never recomputed (LAE ProductivityPage).
    return { ...rest, from: win.from, to: win.to };
  }, [apiParams, range.from, range.to]);

  return {
    cycle,
    setCycle,
    defaultCycle,
    cycleIsDefault,
    cycleResolved,
    funnel,
    setFunnel,
    source,
    setSource,
    preset,
    day,
    today: T,
    range,
    granularity,
    setPeriod,
    stepDay,
    openSchool,
    openSchoolFunnel,
    setOpenSchool,
    resetFilters,
    apiParams,
    prevParams,
  };
}
